import { publicEnv } from '@/config/public-env.config.mjs';

/**
 * Response shape of Ahamove `GET /api/v4/public/food-order/cities/by-point?lat=&lng=`.
 *
 * Replaces the legacy `/v1/order/city_detail`. Crucially this endpoint returns
 * `slug` directly, so the FE no longer maps a hardcoded city_id → slug table:
 * the slug drives URL routing (`/[locale]/[city]`), `id`/`service_city_id` is
 * the city_id for store queries, and `name_vi_vn` is the display label.
 *
 * Example:
 * ```json
 * {
 *   "id": "DAD",
 *   "name": "Da Nang",
 *   "name_vi_vn": "Đà Nẵng",
 *   "country_id": "VN",
 *   "location": { "type": "Point", "coordinates": [108.202, 16.054] },
 *   "area_id": "48",
 *   "level": 2,
 *   "service_city_id": "DAD",
 *   "level_vn": "Thành phố Trung ương",
 *   "slug": "da-nang"
 * }
 * ```
 */
export interface CityByPointResponse {
  id: string;
  name?: string;
  name_vi_vn?: string;
  country_id?: string;
  location?: {
    type: string;
    coordinates: [number, number];
  };
  level?: number;
  level_vn?: string;
  area_id?: string;
  service_city_id?: string;
  slug?: string;
}

const CITY_BY_POINT_PATH = '/api/v4/public/food-order/cities/by-point';

// Build the endpoint from the environment API base. Works on both the server
// (RSC redirects) and the client (location pickers) since it uses plain fetch.
const CITY_BY_POINT_URL = (() => {
  const base = publicEnv.NEXT_PUBLIC_API_URI.replace(/\/+$/, '');
  return `${base}${CITY_BY_POINT_PATH}`;
})();

// A single address change fans out into multiple resolvers (the location picker
// sets the store city, while its parent sheet resolves the same coords to
// decide navigation). These run concurrently with identical lat/lng, so we
// de-duplicate by coordinate:
//   - `inFlight` collapses concurrent calls into one network request.
//   - `cache` collapses rapid sequential lookups of the same coords (short TTL).
const CACHE_TTL_MS = 30_000;
const inFlight = new Map<string, Promise<CityByPointOutcome>>();
const cache = new Map<string, { value: CityByPointOutcome; expires: number }>();

/**
 * Result of a by-point lookup. `not_serviceable` (HTTP 404 / `NOT_FOUND_CITY`)
 * means the coordinate is outside every food-order city — callers must surface
 * this to the user, NOT silently fall back to a default city. `error` is a
 * network/parse failure where a forced fallback is acceptable.
 */
export type CityByPointOutcome =
  | { status: 'ok'; city: CityByPointResponse }
  | { status: 'not_serviceable' }
  | { status: 'error' };

const fetchCityByPoint = async (
  lat: number,
  lng: number
): Promise<CityByPointOutcome> => {
  try {
    const url = `${CITY_BY_POINT_URL}?lat=${lat}&lng=${lng}`;
    const response = await fetch(url);

    if (!response.ok) {
      // 404 / NOT_FOUND_CITY = the point isn't in any served city.
      if (response.status === 404) return { status: 'not_serviceable' };
      // Some backends return 200/4xx with an error code in the body.
      try {
        const body = (await response.json()) as { code?: string };
        if (body?.code === 'NOT_FOUND_CITY')
          return { status: 'not_serviceable' };
      } catch {
        /* ignore parse error */
      }
      return { status: 'error' };
    }

    const data = (await response.json()) as CityByPointResponse & {
      code?: string;
    };

    if (data?.code === 'NOT_FOUND_CITY') return { status: 'not_serviceable' };
    if (!data?.id) return { status: 'error' };

    return { status: 'ok', city: data };
  } catch (error) {
    console.error('Error in getCityByPointFn:', error);
    return { status: 'error' };
  }
};

/**
 * Resolve the city a coordinate belongs to via Ahamove's `cities/by-point` API.
 *
 * The returned `id` / `service_city_id` is the authoritative city_id and `slug`
 * is the authoritative routing slug — no hardcoded city table involved.
 *
 * Concurrent/rapid calls for the same coordinates are de-duplicated (see the
 * caches above). Successful AND not-serviceable results are cached (both are
 * stable for a coordinate); transient errors are NOT cached so they can retry.
 */
export const getCityByPointFn = async (
  lat: number,
  lng: number
): Promise<CityByPointOutcome> => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { status: 'error' };
  }

  const key = `${lat},${lng}`;

  const cached = cache.get(key);
  if (cached && cached.expires > Date.now()) return cached.value;

  const pending = inFlight.get(key);
  if (pending) return pending;

  const promise = fetchCityByPoint(lat, lng).finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, promise);

  const result = await promise;

  // Cache stable outcomes (ok / not_serviceable); let errors retry next time.
  if (result.status !== 'error') {
    cache.set(key, { value: result, expires: Date.now() + CACHE_TTL_MS });
  }

  return result;
};
